﻿import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation
} from 'react-router-dom';
import React, { useContext, useEffect, useRef } from 'react';
import KnowledgeAssistantPage from './components/pages/KnowledgeAssistantPage';
import { ToastContainer } from 'react-toastify';
import Layout from './components/shared/Layout';
import HomePage from './components/pages/HomePage';
import TagViewPage from './components/pages/TagViewPage';
import ArticlePage from './components/pages/ArticlePage';
import NotFoundPage from './components/pages/NotFoundPage';
import useBotPagesConfig from './hooks/useBotPagesConfig';
import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate
} from '@azure/msal-react';
import SignIn from './components/shared/SignIn';
import {
    ThreadContext,
    ThreadProvider
} from './components/knowledge/ThreadContext.tsx';
import TelemetryProvider from './components/TelemetryProvider.tsx';
import isNullOrWhiteSpace from './lib/isNullOrWhiteSpace.ts';
import ArticlesByTopicsPage from './components/pages/ArticleByTopicsPage.tsx';

const ClearThreadOnRouteChange: React.FC = () => {
    const location = useLocation();
    const { setThreadId } = useContext(ThreadContext);
    const prevPath = useRef(location.pathname);

    useEffect(() => {
        if (prevPath.current !== location.pathname) {
            // Route has changed, clear threadInfo
            setThreadId(null);
        }
        prevPath.current = location.pathname;
    }, [location, setThreadId]);

    return null;
};

const App: React.FC = () => {
    const { requiresAuthentication, applicationInsightsKey } =
        useBotPagesConfig();

    const enabled = isNullOrWhiteSpace(applicationInsightsKey) === false;

    const routes = [
        <Route key='home' path='/' element={<HomePage />} />,
        <Route
            key='assistant'
            path='/assistant'
            element={<KnowledgeAssistantPage />}
        />,
        <Route
            key='topic'
            path='/topic/:tagName'
            element={<TagViewPage />}
        />,
        <Route key='article' path='/article/*' element={<ArticlePage />} />,
        <Route
            key='related-articles'
            path='/related_articles'
            element={<ArticlesByTopicsPage />}
        />
    ];

    if (requiresAuthentication) {
        routes.push(
            <Route
                key='auth'
                path='/auth'
                element={<Navigate to={'/'} replace />}
            />
        );
    }

    routes.push(
        <Route key='not-found' path='*' element={<NotFoundPage />} />
    );

    return (
        <ThreadProvider>
            <BrowserRouter>
                <ClearThreadOnRouteChange />
                <TelemetryProvider
                    isDisabled={!enabled}
                    instrumentationKey={applicationInsightsKey ?? null}
                    appId={'helpfruit-pages'}
                >
                    <Layout>
                        <ToastContainer pauseOnFocusLoss={false} />
                        {requiresAuthentication ? (
                            <>
                                <AuthenticatedTemplate>
                                    <Routes>{routes}</Routes>
                                </AuthenticatedTemplate>
                                <UnauthenticatedTemplate>
                                    <Routes>
                                        <Route path='*' element={<SignIn />} />
                                    </Routes>
                                </UnauthenticatedTemplate>
                            </>
                        ) : (
                            <Routes>{routes}</Routes>
                        )}
                    </Layout>
                </TelemetryProvider>
            </BrowserRouter>
        </ThreadProvider>
    );
};

export default App;
